import { useTranslation } from 'react-i18next';
import PageHeader from '@casops/components/layout/page-header/PageHeader';
import StandardPage from '@casops/templates/standard-page/StandardPage';
import { QueryTag, TagList } from '@casops/store/api';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import {
  AssetDeviceType,
  RegisteredAssetBasicModel,
  RegisteredAssetListFilters,
  RegisteredAssetModel,
  useListAssetsQuery,
} from '@casops/store/apis/registeredAssetsApi';
import { ModelName } from '@casops/store/api';
import FilterButton from '@casops/components/inputs/filter-button/FilterButton';
import useFilters from '@casops/filters/useFilters';
import { FilterGroup } from '@casops/filters/types';
import { useNavigate } from 'react-router-dom';
import { useAssetsColumnDefs } from './useAssetsColumnDefs';
import GridActionButton from '@casops/components/inputs/grid-action-button/GridActionButton';
import { Permission } from '@casops/permissions';
import { useCallback, useMemo } from 'react';
import { openSidePanel, SidePanel } from '@casops/store/slices/globalSlice';
import { useAppDispatch } from '@casops/store/hooks';
import { ASSETS_PAGE_SLICE_KEY } from '@casops/store/slices/gridSlice';
import { CellClickedEvent, ColDef, IsRowSelectable } from 'ag-grid-community';

type Model = RegisteredAssetModel;

const modelName = ModelName.Asset;
const permission = Permission.AssetManagement;

const tagsToClearOnRefresh = [
  { type: QueryTag.AssetRegistered, id: TagList },
  { type: QueryTag.AssetGroup, id: TagList },
  { type: QueryTag.ConfigurationGroup, id: TagList },
];

const getRowId = (data: RegisteredAssetBasicModel) => String(data.id);
const onRowClick = () => undefined; // Do nothing. Just an easy way to make the cursor a pointer for the whole row.

const isRowSelectable: IsRowSelectable<Model> = ({ data }): boolean =>
  data?.hw !== AssetDeviceType.PPU;

export function AssetsPage() {
  const { t } = useTranslation();
  const filters = useFilters<RegisteredAssetListFilters>(FilterGroup.AssetsList);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const columnDefs = useAssetsColumnDefs();

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { data, ...requestRemainder } = useListAssetsQuery(
    { query: filters },
    {
      refetchOnMountOrArgChange: 60,
    },
  );

  const { items, countAll, countFilter } = data ?? {};

  const { hasData, noDataDescription } = useTableData<Model>({
    data: items,
    ...requestRemainder,
    noResultsDescription,
  });

  const onClickShowAssetsAssignConfigurationGroupPanel = useCallback(
    (ids: number[]) => {
      dispatch(
        openSidePanel({
          sidePanel: SidePanel.AssetsAssignConfigurationGroup,
          bulkModelIds: ids.map(String),
          bulkEditMode: true,
        }),
      );
    },
    [dispatch],
  );

  // The cell click is used in place of a row click.
  const onCellClicked = useCallback(
    (event: CellClickedEvent<Model>) => {
      if (event.data) {
        navigate(`/assets/asset/${event.data.id}`);
      }
    },
    [navigate],
  );

  // Applying an onCellClicked to every cell acts much like a row click, but automatically avoids the selection checkbox cell.
  const defaultColDef = useMemo<ColDef<Model>>(
    () => ({
      onCellClicked,
    }),
    [onCellClicked],
  );

  return (
    <StandardPage
      header={
        <PageHeader
          title={t('Model.Collection.Title', { modelName })}
          tagsToClearOnRefresh={tagsToClearOnRefresh}
        />
      }
    >
      <CollectionView<Model>
        sliceKey={ASSETS_PAGE_SLICE_KEY}
        modelName={modelName}
        columnDefs={columnDefs}
        rowData={items}
        getRowId={getRowId}
        hasData={hasData}
        rowSelectMode="multiRow"
        isRowSelectable={isRowSelectable}
        noDataDescription={noDataDescription}
        defaultColDef={defaultColDef}
        onRowClick={onRowClick}
        filterProps={{
          filterGroup: FilterGroup.AssetsList,
          countAll,
          countFilter,
        }}
        actions={
          <>
            <GridActionButton
              sliceKey={ASSETS_PAGE_SLICE_KEY}
              permission={permission}
              onClick={onClickShowAssetsAssignConfigurationGroupPanel}
              icon="edit"
              label={t('Assign')}
              disabled={!items?.length}
            />
            <FilterButton filters={FilterGroup.AssetsList} disabled={false} />
          </>
        }
      />
    </StandardPage>
  );
}

export default AssetsPage;
